import { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { api, fmtBRL, fmtInt } from "@/lib/api";
import { apexBase, buildParams, useChartTheme, PALETTE } from "./pcShared";
import { TrendUp, TrendDown, Warning, Info } from "@phosphor-icons/react";

const GRAN_OPTIONS = [["daily", "Dia"], ["weekly", "Semana"], ["monthly", "Mês"]];

const TONE = {
  up: { icon: TrendUp, cls: "border-green-500 bg-green-50 text-green-700" },
  down: { icon: TrendDown, cls: "border-red-500 bg-red-50 text-red-700" },
  warn: { icon: Warning, cls: "border-yellow-500 bg-yellow-50 text-yellow-700" },
  info: { icon: Info, cls: "border-blue-400 bg-blue-50 text-blue-700" },
};

function Insights({ insights }) {
  if (!insights?.length) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2" data-testid="pc-insights">
      {insights.map((i, idx) => {
        const t = TONE[i.tone] || TONE.info;
        const Icon = t.icon;
        return (
          <div key={`${i.tone}-${i.text}`} className={`border-l-4 px-3 py-2 text-xs flex items-start gap-2 rounded ${t.cls}`} data-testid={`pc-insight-${idx}`}>
            <Icon size={16} weight="bold" className="shrink-0 mt-0.5" />
            <span>{i.text}</span>
          </div>
        );
      })}
    </div>
  );
}

function Panel({ title, children, testid, right }) {
  return (
    <div className="tile p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="overline text-[10px] text-zinc-500">{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

export default function OverviewTab({ applied }) {
  const dark = useChartTheme();
  const [charts, setCharts] = useState(null);
  const [insights, setInsights] = useState(null);
  const [gran, setGran] = useState("daily");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = buildParams(applied);
    Promise.all([
      api.get("/production-reports/charts", { params }),
      api.get("/production-reports/insights", { params }),
    ]).then(([c, i]) => {
      if (!alive) return;
      setCharts(c.data);
      setInsights(i.data.insights);
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [applied]);

  // Memoização: evita recriar objetos de opções/séries dos charts a cada render
  const base = useMemo(() => apexBase(dark), [dark]);
  const serie = useMemo(() => charts?.series?.[gran] || [], [charts, gran]);
  const lineOpts = useMemo(() => ({
    ...base,
    chart: { ...base.chart, type: "line", zoom: { enabled: false } },
    stroke: { curve: "smooth", width: [3, 2] },
    xaxis: { categories: serie.map((r) => r.d), labels: { rotate: -45, style: { fontSize: "10px" } } },
    yaxis: [
      { title: { text: "Peças" }, labels: { formatter: (v) => fmtInt(v) } },
      { opposite: true, title: { text: "Pedidos" } },
    ],
  }), [base, serie]);
  const lineSeries = useMemo(() => [
    { name: "Peças produzidas", data: serie.map((r) => r.pieces) },
    { name: "Pedidos", data: serie.map((r) => r.orders) },
  ], [serie]);
  const heatSeries = useMemo(() => {
    const heat = charts?.heatmap;
    if (!heat) return [];
    return heat.weekdays.map((w, i) => ({
      name: w,
      data: heat.matrix[i].map((c, h) => ({ x: `${h}h`, y: c })),
    })).reverse();
  }, [charts]);

  if (loading || !charts) {
    return <div className="tile p-8 text-center text-zinc-400 animate-pulse" data-testid="pc-overview-loading">Carregando gráficos...</div>;
  }

  const collabBar = charts.bars.by_collaborator;
  const stationBar = charts.bars.by_station;
  const custBar = charts.bars.by_customer;

  const pie = (data, testid, title) => (
    <Panel title={title} testid={testid}>
      {data.length === 0 ? <Empty /> : (
        <Chart type="donut" height={220}
          series={data.map((d) => d.value)}
          options={{ ...base, labels: data.map((d) => d.name), legend: { position: "bottom", fontSize: "10px" }, dataLabels: { enabled: true, formatter: (v) => `${v.toFixed(0)}%` } }} />
      )}
    </Panel>
  );

  const radar = charts.radar;

  return (
    <div className="space-y-3" data-testid="pc-overview-tab">
      <Insights insights={insights} />

      <Panel title="EVOLUÇÃO DA PRODUÇÃO" testid="pc-chart-evolution"
        right={
          <div className="flex gap-1">
            {GRAN_OPTIONS.map(([k, l]) => (
              <button key={k} onClick={() => setGran(k)}
                className={`text-[10px] px-2 py-0.5 border rounded ${gran === k ? "bg-black text-white border-black" : "border-zinc-300"}`}
                data-testid={`pc-gran-${k}`}>{l}</button>
            ))}
          </div>
        }>
        {serie.length === 0 ? <Empty /> : (
          <Chart type="line" height={280} series={lineSeries} options={lineOpts} />
        )}
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Panel title="PRODUÇÃO POR COLABORADOR (PEÇAS)" testid="pc-chart-by-collaborator">
          {collabBar.length === 0 ? <Empty /> : (
            <Chart type="bar" height={Math.max(200, collabBar.length * 28)}
              series={[{ name: "Peças", data: collabBar.map((r) => r.pieces) }]}
              options={{ ...base, plotOptions: { bar: { horizontal: true, borderRadius: 3 } }, xaxis: { categories: collabBar.map((r) => r.name) }, colors: [PALETTE[0]] }} />
          )}
        </Panel>
        <Panel title="MOVIMENTOS POR POSTO" testid="pc-chart-by-station">
          {stationBar.length === 0 ? <Empty /> : (
            <Chart type="bar" height={Math.max(200, stationBar.length * 34)}
              series={[{ name: "Movimentos", data: stationBar.map((r) => r.movements) }]}
              options={{ ...base, plotOptions: { bar: { horizontal: true, borderRadius: 3 } }, xaxis: { categories: stationBar.map((r) => r.name) }, colors: [PALETTE[1]] }} />
          )}
        </Panel>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {pie(charts.pies.by_sector, "pc-pie-sector", "DISTRIBUIÇÃO POR SETOR")}
        {pie(charts.pies.by_status, "pc-pie-status", "PIPELINE POR STATUS")}
        {pie(charts.pies.by_type, "pc-pie-type", "PEÇAS POR TIPO DE COPO")}
        {pie(charts.pies.by_marketplace, "pc-pie-marketplace", "PEDIDOS POR MARKETPLACE")}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2">
          <Panel title="HEATMAP · DIA DA SEMANA × HORA (MOVIMENTOS)" testid="pc-heatmap">
            <Chart type="heatmap" height={260}
              series={heatSeries}
              options={{
                ...base,
                dataLabels: { enabled: false },
                colors: [PALETTE[0]],
                plotOptions: { heatmap: { shadeIntensity: 0.6, radius: 2 } },
              }} />
          </Panel>
        </div>
        <Panel title="META × PONTUALIDADE" testid="pc-gauge">
          <Chart type="radialBar" height={230}
            series={[charts.gauge.on_time_pct]}
            options={{
              ...base,
              labels: ["No prazo"],
              plotOptions: {
                radialBar: {
                  hollow: { size: "60%" },
                  dataLabels: { value: { fontSize: "24px", formatter: (v) => `${v}%` } },
                },
              },
              colors: [charts.gauge.on_time_pct >= 70 ? "#39D98A" : charts.gauge.on_time_pct >= 40 ? "#FFC700" : "#FF5E5E"],
            }} />
          <div className="text-[10px] text-zinc-500 text-center">% de pedidos finalizados dentro da data limite</div>
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Panel title="RADAR · EFICIÊNCIA DOS COLABORADORES" testid="pc-radar">
          {radar.collaborators.length === 0 ? <Empty /> : (
            <Chart type="radar" height={320}
              series={radar.collaborators.map((c) => ({ name: c.name, data: c.dims }))}
              options={{ ...base, xaxis: { categories: radar.dims }, stroke: { width: 2 }, fill: { opacity: 0.1 }, markers: { size: 2 }, legend: { fontSize: "10px" } }} />
          )}
        </Panel>
        <Panel title="TOP CLIENTES · VALOR PRODUZIDO" testid="pc-chart-by-customer">
          {custBar.length === 0 ? <Empty /> : (
            <Chart type="bar" height={Math.max(200, custBar.length * 28)}
              series={[{ name: "Valor", data: custBar.map((r) => r.value) }]}
              options={{
                ...base, plotOptions: { bar: { horizontal: true, borderRadius: 3 } },
                xaxis: { categories: custBar.map((r) => r.name), labels: { formatter: (v) => fmtBRL(v) } },
                colors: [PALETTE[2]],
              }} />
          )}
        </Panel>
      </div>
    </div>
  );
}

function Empty() {
  return <div className="text-center text-zinc-400 text-xs py-8">Sem dados no período</div>;
}
